import { Button, Flex, Typography } from "antd"
import React, { useEffect, useLayoutEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { useGuideTour } from "./GuideTourContext.tsx"
import { measureElement } from "./measure-element.ts"

const { Text, Title } = Typography

const SPOTLIGHT_PADDING = 8
// A step's target may still be loading in (a fresh navigation, a fetch in
// flight) — poll briefly rather than render a tour that points at nothing,
// and give up onto the next step so a missing element can never strand it.
// A step with no `target` at all (GuideTourStep.target omitted) skips this
// entirely and renders centered for as long as it's active — that's a
// deliberate "this doesn't live behind one nav item" step, not a failure.
const TARGET_POLL_MS = 150
const TARGET_TIMEOUT_MS = 4000

type Rect = { top: number; left: number; width: number; height: number }

const measure = (el: Element): Rect => measureElement(el)

const useTargetRect = (targetKey: string | undefined, onMissing: () => void) => {
  const [rect, setRect] = useState<Rect | null>(null)

  useEffect(() => {
    setRect(null)
    if (!targetKey) return

    let cancelled = false
    const startedAt = Date.now()

    const tick = () => {
      if (cancelled) return
      const el = document.querySelector(`[data-guide-target="${targetKey}"]`)
      if (el) {
        setRect(measure(el))
        return
      }
      if (Date.now() - startedAt > TARGET_TIMEOUT_MS) {
        onMissing()
        return
      }
      window.setTimeout(tick, TARGET_POLL_MS)
    }
    tick()

    return () => {
      cancelled = true
    }
  }, [targetKey])

  useLayoutEffect(() => {
    if (!targetKey) return
    const reMeasure = () => {
      const el = document.querySelector(`[data-guide-target="${targetKey}"]`)
      if (el) setRect(measure(el))
    }
    window.addEventListener("resize", reMeasure)
    window.addEventListener("scroll", reMeasure, true)
    return () => {
      window.removeEventListener("resize", reMeasure)
      window.removeEventListener("scroll", reMeasure, true)
    }
  }, [targetKey])

  return rect
}

export const GuideTourOverlay = () => {
  const { t } = useTranslation()
  const { activeTour, activeStep, stepIndex, totalSteps, next, back, skip } =
    useGuideTour()
  const rect = useTargetRect(activeStep?.target, next)

  if (!activeTour || !activeStep) return null

  const placement = activeStep.placement ?? "bottom"
  const cardStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        top:
          placement === "bottom"
            ? rect.top + rect.height + SPOTLIGHT_PADDING * 2
            : placement === "top"
              ? undefined
              : Math.max(16, rect.top),
        bottom:
          placement === "top"
            ? window.innerHeight - rect.top + SPOTLIGHT_PADDING * 2
            : undefined,
        left:
          placement === "left"
            ? undefined
            : Math.min(rect.left, window.innerWidth - 360),
        right: placement === "left" ? window.innerWidth - rect.left + 16 : undefined
      }
    : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }

  return createPortal(
    <div
      className="fixed inset-0 z-[1200]"
      role="dialog"
      aria-label={t(activeTour.titleKey)}
    >
      {rect && (
        <div
          className="pointer-events-none fixed rounded-md transition-all duration-150"
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
            border: "2px solid var(--ant-color-primary, #2A7D8C)"
          }}
        />
      )}
      {!rect && (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(15, 23, 42, 0.55)" }}
        />
      )}

      <div
        className={cn("w-[340px] rounded-lg bg-white p-4 shadow-xl border border-border")}
        style={cardStyle}
      >
        <Flex vertical gap={8}>
          <Text type="secondary" className="text-xs">
            {t("guide.tour.common.stepCounter", {
              current: stepIndex + 1,
              total: totalSteps
            })}
          </Text>
          <Title level={5} className="!mb-0">
            {t(activeStep.titleKey)}
          </Title>
          <Text>{t(activeStep.bodyKey)}</Text>
          <Flex justify="space-between" align="center" className="mt-2">
            <Button type="text" size="small" onClick={skip}>
              {t("guide.tour.common.skip")}
            </Button>
            <Flex gap={8}>
              {stepIndex > 0 && (
                <Button size="small" onClick={back}>
                  {t("guide.tour.common.back")}
                </Button>
              )}
              <Button type="primary" size="small" onClick={next}>
                {stepIndex >= totalSteps - 1
                  ? t("guide.tour.common.finish")
                  : t("guide.tour.common.next")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </div>
    </div>,
    document.body
  )
}
